/**
 * A hand's position ON THE JOB, per phase (API 0426, `job_crew_positions`).
 *
 * Every crew read carries `positions: [{ phase, positionId, positionName,
 * lineItemTypes }]` — camelCase on the office routes, snake_case on
 * `/employee/*`. One NULL-phase entry means the whole job; otherwise one entry
 * per phase ('wash' | 'run'). Never both for one hand (the API's exclusion
 * constraint). An empty array means no job position has been picked, which is
 * NOT the same as the hand's PRIMARY position (`position_name`) — that is what
 * the hand usually does, this is what they are doing here.
 *
 * The only writer is PATCH /dispatch/jobs/:id/crew's `positions` key.
 */

export type JobPositionPhase = 'wash' | 'run';

export interface JobCrewPosition {
  /** null = the whole job. */
  phase: JobPositionPhase | null;
  positionId: string;
  positionName: string | null;
  /**
   * The line item types this position works (API 0425), from the POSITION's
   * mapping. `[]` when the position maps none — or when the payload did not
   * carry the key, which only a pre-0426 cache can produce; either way the
   * position claims no line, which is the safe reading for a suggestion.
   */
  lineItemTypes: string[];
}

/** Both casings, and anything malformed dropped rather than guessed at. */
export function readJobPositions(raw: unknown): JobCrewPosition[] {
  if (!Array.isArray(raw)) return [];
  const out: JobCrewPosition[] = [];
  for (const e of raw) {
    if (!e || typeof e !== 'object') continue;
    const r = e as Record<string, unknown>;
    const id = r.positionId ?? r.position_id;
    if (typeof id !== 'string' || !id) continue;
    const phase = r.phase === 'wash' || r.phase === 'run' ? r.phase : null;
    const name = r.positionName ?? r.position_name;
    const types = r.lineItemTypes ?? r.line_item_types;
    out.push({
      phase,
      positionId: id,
      positionName: typeof name === 'string' && name.trim() ? name.trim() : null,
      lineItemTypes: Array.isArray(types) ? types.filter((t): t is string => typeof t === 'string' && t !== '') : [],
    });
  }
  return out;
}

const PHASE_ORDER: Record<string, number> = { wash: 1, run: 2 };

/**
 * The job position as one line of display copy, or null when none is picked.
 *
 *   whole job                 → "Tong Hand"
 *   per phase                 → "Wash Truck Swamper (wash) · Tong Hand (run)"
 *   same position both phases → "Tong Hand" — the phase tags would add nothing
 *   one phase only            → "Tong Hand (run)"
 *
 * Wash before run because that is the order the work happens and the order the
 * board draws the two cards. A row whose position name did not arrive is left
 * out rather than printed as a blank; if that leaves nothing, the answer is null
 * and the caller falls back.
 */
export function jobPositionText(raw: unknown): string | null {
  const rows = readJobPositions(raw).filter((p) => p.positionName);
  if (rows.length === 0) return null;
  const whole = rows.find((p) => p.phase === null);
  if (whole) return whole.positionName;
  const phased = [...rows].sort((a, b) => PHASE_ORDER[a.phase!] - PHASE_ORDER[b.phase!]);
  // Collapse only when BOTH phases hold the same position. A lone phase keeps
  // its tag: "Tong Hand" untagged would claim the wash too.
  if (phased.length === 2 && phased[0].positionName === phased[1].positionName) return phased[0].positionName;
  return phased.map((p) => `${p.positionName} (${p.phase})`).join(' · ');
}

/**
 * The job position to print on ONE dispatch card's chip. A card that is one
 * phase of a split job shows that phase's position (or the whole-job one); a
 * card drawing the whole job shows the full text. null when nothing applies,
 * so the chip draws no tag at all rather than an empty one.
 */
export function jobPositionForCard(
  raw: unknown,
  cardType: JobPositionPhase | undefined,
  split: boolean,
): string | null {
  if (!cardType || !split) return jobPositionText(raw);
  const rows = readJobPositions(raw).filter((p) => p.positionName);
  const hit = rows.find((p) => p.phase === null) ?? rows.find((p) => p.phase === cardType);
  return hit?.positionName ?? null;
}
